import * as i0 from '@angular/core';
import { Injectable, EventEmitter, PLATFORM_ID, Component, Inject, Input, Output, ViewChild, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
const _c0 = ["streamEl"];
let CloudflareStreamService = /*#__PURE__*/(() => {
  class CloudflareStreamService {
    constructor() {}
  }
  CloudflareStreamService.ɵfac = function CloudflareStreamService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CloudflareStreamService)();
  };
  CloudflareStreamService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: CloudflareStreamService,
    factory: CloudflareStreamService.ɵfac,
    providedIn: 'root'
  });
  return CloudflareStreamService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * We have to export this interface so the module can be compiled
 * with the AOT flag enabled.
 *
 * https://github.com/angular/angular/issues/14050
 */
let DocumentWrapper = /*#__PURE__*/(() => {
  class DocumentWrapper {
    get nativeDocument() {
      return document;
    }
  }
  DocumentWrapper.ɵfac = function DocumentWrapper_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DocumentWrapper)();
  };
  DocumentWrapper.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DocumentWrapper,
    factory: DocumentWrapper.ɵfac
  });
  return DocumentWrapper;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function getDocumentWrapper() {
  return new DocumentWrapper();
}
const propertyProps = ['autoplay', 'controls', 'currentTime', 'primaryColor', 'muted', 'loop', 'volume', 'preload', 'defaultTextTrack'];
let CloudflareStreamComponent = /*#__PURE__*/(() => {
  class CloudflareStreamComponent {
    constructor(renderer2, doc, platformId) {
      this.renderer2 = renderer2;
      this.doc = doc;
      this.platformId = platformId;
      // tslint:disable: no-output-native
      /**
       * Sent when playback is aborted; for example, if the media is playing and is restarted from the beginning, this event is sent.
       */
      this.abort = new EventEmitter();
      /**
       * Sent when enough data is available that the media can be played, at least for a couple of frames.
       */
      this.canplay = new EventEmitter();
      /**
       * Sent when the entire media can be played without interruption, assuming the download rate remains at least at the current level. It will also be fired when playback is toggled between paused and playing. Note: Manually setting the currentTime will eventually fire a canplaythrough event in firefox. Other browsers might not fire this event.
       */
      this.canplaythrough = new EventEmitter();
      /**
       * The metadata has loaded or changed, indicating a change in duration of the media. This is sent, for example, when the media has loaded enough that the duration is known.
       */
      this.durationchange = new EventEmitter();
      /**
       * Sent when playback completes.
       */
      this.ended = new EventEmitter();
      /**
       * Sent when an error occurs. (e.g. the video has not finished encoding yet, or the video fails to load due to an incorrect signed URL)
       */
      this.error = new EventEmitter();
      /**
       * The first frame of the media has finished loading.
       */
      this.loadeddata = new EventEmitter();
      /**
       * The media’s metadata has finished loading; all attributes now contain as much useful information as they’re going to.
       */
      this.loadedmetadata = new EventEmitter();
      /**
       * Sent when loading of the media begins.
       */
      this.loadstart = new EventEmitter();
      /**
       * Sent when the playback state is changed to paused (paused property is true).
       */
      this.pause = new EventEmitter();
      /**
       * Sent when the playback state is no longer paused, as a result of the play method, or the autoplay attribute.
       */
      this.play = new EventEmitter();
      /**
       * Sent when the media has enough data to start playing, after the play event, but also when recovering from being stalled, when looping media restarts, and after seeked, if it was playing before seeking.
       */
      this.playing = new EventEmitter();
      /**
       * Sent periodically to inform interested parties of progress downloading the media. Information about the current amount of the media that has been downloaded is available in the media element’s buffered attribute.
       */
      this.progress = new EventEmitter();
      /**
       * Sent when the playback speed changes.
       */
      this.ratechange = new EventEmitter();
      /**
       * Sent when a seek operation completes.
       */
      this.seeked = new EventEmitter();
      /**
       * Sent when a seek operation begins.
       */
      this.seeking = new EventEmitter();
      /**
       * Sent when the user agent is trying to fetch media data, but data is unexpectedly not forthcoming.
       */
      this.stalled = new EventEmitter();
      /**
       * Sent when loading of the media is suspended; this may happen either because the download has completed or because it has been paused for any other reason.
       */
      this.suspend = new EventEmitter();
      /**
       * The time indicated by the element’s currentTime attribute has changed.
       */
      this.timeupdate = new EventEmitter();
      /**
       * Sent when the audio volume changes (both when the volume is set and when the muted attribute is changed).
       */
      this.volumechange = new EventEmitter();
      /**
       * Sent when the requested operation (such as playback) is delayed pending the completion of another operation (such as a seek).
       */
      this.waiting = new EventEmitter();
      /**
       * Fires when ad-url attribute is present and the ad begins playback
       */
      this.streamAdStart = new EventEmitter();
      /**
       * Fires when ad-url attribute is present and the ad finishes playback
       */
      this.streamAdEnd = new EventEmitter();
      /**
       * Fires when ad-url attribute is present and the ad took too long to load.
       */
      this.streamAdTimeout = new EventEmitter();
    }
    ngOnChanges(changes) {
      // ngOnChanges fires when the component mounts but before the view
      // is initialized, so we need to bail when this fires for the first
      // time.
      if (!this.streamEl) {
        return;
      }
      // convert SimpleChanges into a Record that has currentValues to be
      // synced onto streamEl
      this.syncProperties(Object.keys(changes).reduce((acc, key) => ({
        ...acc,
        [key]: changes[key].currentValue
      }), {}));
    }
    /**
     * Method to take an object and sync keys from propertyProps onto
     * the stream element
     */
    syncProperties(properties) {
      // iterate over the propertyProps and assign them to the streamEl
      propertyProps.forEach(prop => {
        // only assign the property if it is present
        if (properties.hasOwnProperty(prop)) {
          this.streamEl.nativeElement[prop] = properties[prop];
        }
      });
    }
    ngAfterViewInit() {
      // streamEl is first available within ngAfterViewInit, so we need to sync
      // properties onto the element
      this.syncProperties(
      // pluck current propertyProps off of the component instance to sync them to streamEl
      propertyProps.reduce((acc, prop) =>
      // skip values that are undefined
      this[prop] === undefined ? acc : {
        ...acc,
        [prop]: this[prop]
      }, {}));
      this.loadStreamScript();
    }
    get isBrowser() {
      return isPlatformBrowser(this.platformId);
    }
    loadStreamScript() {
      if (!this.isBrowser) return;
      this.streamScript = document.createElement('script');
      this.streamScript.setAttribute('data-cfasync', 'false');
      this.streamScript.setAttribute('defer', 'true');
      this.streamScript.setAttribute('type', 'text/javascript');
      this.streamScript.setAttribute('src', 'https://embed.cloudflarestream.com/embed/r4xu.fla9.latest.js');
      this.renderer2.appendChild(this.doc.nativeDocument.head, this.streamScript);
    }
    cleanUpStreamScript() {
      if (!this.isBrowser) return;
      this.renderer2.removeChild(this.doc.nativeDocument.head, this.streamScript);
    }
    ngOnDestroy() {
      this.cleanUpStreamScript();
    }
  }
  CloudflareStreamComponent.ɵfac = function CloudflareStreamComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CloudflareStreamComponent)(i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DocumentWrapper), i0.ɵɵdirectiveInject(PLATFORM_ID));
  };
  CloudflareStreamComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: CloudflareStreamComponent,
    selectors: [["cloudflare-stream"]],
    viewQuery: function CloudflareStreamComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.streamEl = _t.first);
      }
    },
    inputs: {
      adUrl: "adUrl",
      autoplay: "autoplay",
      controls: "controls",
      currentTime: "currentTime",
      defaultTextTrack: "defaultTextTrack",
      height: "height",
      loop: "loop",
      muted: "muted",
      poster: "poster",
      preload: "preload",
      primaryColor: "primaryColor",
      src: "src",
      volume: "volume",
      width: "width"
    },
    outputs: {
      abort: "abort",
      canplay: "canplay",
      canplaythrough: "canplaythrough",
      durationchange: "durationchange",
      ended: "ended",
      error: "error",
      loadeddata: "loadeddata",
      loadedmetadata: "loadedmetadata",
      loadstart: "loadstart",
      pause: "pause",
      play: "play",
      playing: "playing",
      progress: "progress",
      ratechange: "ratechange",
      seeked: "seeked",
      seeking: "seeking",
      stalled: "stalled",
      suspend: "suspend",
      timeupdate: "timeupdate",
      volumechange: "volumechange",
      waiting: "waiting",
      streamAdStart: "streamAdStart",
      streamAdEnd: "streamAdEnd",
      streamAdTimeout: "streamAdTimeout"
    },
    standalone: false,
    features: [i0.ɵɵNgOnChangesFeature],
    decls: 2,
    vars: 12,
    consts: [["streamEl", ""], [3, "play", "abort", "canplay", "canplaythrough", "durationchange", "ended", "error", "loadeddata", "loadedmetadata", "loadstart", "pause", "playing", "progress", "ratechange", "seeked", "seeking", "stalled", "suspend", "timeupdate", "volumechange", "waiting", "stream-adstart", "stream-adend", "stream-adtimeout"]],
    template: function CloudflareStreamComponent_Template(rf, ctx) {
      if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelementStart(0, "stream", 1, 0);
        i0.ɵɵlistener("play", function CloudflareStreamComponent_Template_stream_play_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.play.emit($event));
        })("abort", function CloudflareStreamComponent_Template_stream_abort_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.abort.emit($event));
        })("canplay", function CloudflareStreamComponent_Template_stream_canplay_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.canplay.emit($event));
        })("canplaythrough", function CloudflareStreamComponent_Template_stream_canplaythrough_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.canplaythrough.emit($event));
        })("durationchange", function CloudflareStreamComponent_Template_stream_durationchange_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.durationchange.emit($event));
        })("ended", function CloudflareStreamComponent_Template_stream_ended_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.ended.emit($event));
        })("error", function CloudflareStreamComponent_Template_stream_error_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.error.emit($event));
        })("loadeddata", function CloudflareStreamComponent_Template_stream_loadeddata_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.loadeddata.emit($event));
        })("loadedmetadata", function CloudflareStreamComponent_Template_stream_loadedmetadata_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.loadedmetadata.emit($event));
        })("loadstart", function CloudflareStreamComponent_Template_stream_loadstart_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.loadstart.emit($event));
        })("pause", function CloudflareStreamComponent_Template_stream_pause_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.pause.emit($event));
        })("play", function CloudflareStreamComponent_Template_stream_play_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.play.emit($event));
        })("playing", function CloudflareStreamComponent_Template_stream_playing_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.playing.emit($event));
        })("progress", function CloudflareStreamComponent_Template_stream_progress_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.progress.emit($event));
        })("ratechange", function CloudflareStreamComponent_Template_stream_ratechange_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.ratechange.emit($event));
        })("seeked", function CloudflareStreamComponent_Template_stream_seeked_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.seeked.emit($event));
        })("seeking", function CloudflareStreamComponent_Template_stream_seeking_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.seeking.emit($event));
        })("stalled", function CloudflareStreamComponent_Template_stream_stalled_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.stalled.emit($event));
        })("suspend", function CloudflareStreamComponent_Template_stream_suspend_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.suspend.emit($event));
        })("timeupdate", function CloudflareStreamComponent_Template_stream_timeupdate_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.timeupdate.emit($event));
        })("volumechange", function CloudflareStreamComponent_Template_stream_volumechange_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.volumechange.emit($event));
        })("waiting", function CloudflareStreamComponent_Template_stream_waiting_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.waiting.emit($event));
        })("stream-adstart", function CloudflareStreamComponent_Template_stream_stream_adstart_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.streamAdStart.emit($event));
        })("stream-adend", function CloudflareStreamComponent_Template_stream_stream_adend_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.streamAdEnd.emit($event));
        })("stream-adtimeout", function CloudflareStreamComponent_Template_stream_stream_adtimeout_0_listener($event) {
          i0.ɵɵrestoreView(_r1);
          return i0.ɵɵresetView(ctx.streamAdTimeout.emit($event));
        });
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵattribute("ad-url", ctx.adUrl)("src", ctx.src)("autoplay", ctx.autoplay)("controls", ctx.controls)("default-text-track", ctx.defaultTextTrack)("loop", ctx.loop)("preload", ctx.preload)("height", ctx.height)("width", ctx.width)("poster", ctx.poster)("primary-color", ctx.primaryColor)("muted", ctx.muted);
      }
    },
    encapsulation: 2
  });
  return CloudflareStreamComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CloudflareStreamModule = /*#__PURE__*/(() => {
  class CloudflareStreamModule {}
  CloudflareStreamModule.ɵfac = function CloudflareStreamModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CloudflareStreamModule)();
  };
  CloudflareStreamModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: CloudflareStreamModule
  });
  CloudflareStreamModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [{
      provide: DocumentWrapper,
      useFactory: getDocumentWrapper
    }],
    imports: [[]]
  });
  return CloudflareStreamModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of cloudflare-stream
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CloudflareStreamComponent, CloudflareStreamModule, CloudflareStreamService };
